// src/components/pages/BlogList.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
// GlobalStylesから汎用スタイルをインポート (パスはプロジェクト構造に合わせてください)
import {
    PageContainer as BlogListOuterContainer, // ブログページ専用の最上位コンテナとして利用
} from '../styles/GlobalStyles';
import { BlogArticle } from '../types/blog';
import { fetchNotePosts } from '../api/note'; // note APIから記事を取得


// ローカルスタイル定義
const BackToHomeButton = styled.button`
  background-color: #6c757d;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1em;
  margin-bottom: 20px;
  display: block;
  margin-right: auto;

  &:hover {
    background-color: #5a6268;
  }
`;

const BlogListGlobalTitle = styled.h1`
  font-size: 2.5em;
  color:rgb(70, 70, 70);
  text-align: center;
  font-weight: bold;
  margin-top: 20px;
`;
const BlogListContainer = styled(BlogListOuterContainer)`
  padding-top: 10px; // SphereMenuがないので上部に余白
  padding-bottom: 60px;
  max-width: 1200px; // ブログ一覧なので少し広めに
`;

const BlogHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
  padding: 25px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px solid rgba(0, 255, 255, 0.1);

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SearchInput = styled.input`
  padding: 12px 18px;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  background-color:rgb(255, 255, 255);
  color: #0d0d0d;
  font-size: 1em;
  outline-color: cyan;
  width: 100%;
  max-width: 350px;
  transition: border-color 0.3s ease;
`;

const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 35px;
`;

const ArticleCardStyled = styled(motion.a)`
  background-color: rgba(40, 40, 40, 0.7);
  border-radius: 10px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 255, 255, 0.15);
  transition: box-shadow 0.3s ease-in-out;

  &:hover {
    box-shadow: 0 0 25px rgba(0, 255, 255, 0.3);
  }

  /* CardImage (imgタグ) へのホバースタイルをここで定義 */
  &:hover img { /* ArticleCardStyled がホバーされたら、その中にある img 要素の transform を変更 */
    transform: scale(1.05);
  }
`;

const CardImageContainer = styled.div`
  width: 100%;
  padding-top: 56.25%; /* 16:9 アスペクト比 */
  position: relative;
  overflow: hidden;
`;

const CardImage = styled('img')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease-out; /* アニメーションのための transition はこちらに残す */
`;

const CardContent = styled.div`
  padding: 20px 25px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const CardTitle = styled.h3`
  font-size: 1.6em;
  color: #00e5ff;
  margin-bottom: 12px;
  line-height: 1.3;
`;

const CardExcerpt = styled.p`
  font-size: 0.95em;
  color: #c0c0c0;
  line-height: 1.6;
  margin-bottom: 18px;
  flex-grow: 1;
  display: -webkit-box; // 抜粋を数行で省略 (...)
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  font-size: 0.85em;
  color: #888;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const CardCategory = styled.span`
  background-color: rgba(0, 188, 212, 0.2); // 少し透過した背景
  color: #00e5ff; // カテゴリ文字色を明るく
  padding: 4px 10px;
  border-radius: 15px; // 角を丸く
  font-weight: 500;
  font-size: 0.9em;
`;

const formatDate = (dateString: string) => {
    try {
        return new Date(dateString).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    } catch (e) {
        return dateString; // 無効な日付の場合は元の文字列を返す
    }
};

interface ArticleCardProps {
    article: BlogArticle;
    onClick?: (articleId: string) => void; // 記事カードがクリックされたときのハンドラ
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, onClick }) => {
    return (
        <ArticleCardStyled
            href={article.externalUrl || `https://note.com/kida_lw/n/${article.id}`}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
            onClick={() => onClick && onClick(article.id)}
        >
            {article.imageUrl && (
                <CardImageContainer>
                    <CardImage src={article.imageUrl} alt={article.title} />
                </CardImageContainer>
            )}
            <CardContent>
                <CardTitle>{article.title}</CardTitle>
                <CardExcerpt>{article.excerpt}</CardExcerpt>
                <CardMeta>
                    <CardCategory>{article.category}</CardCategory>
                    <span>{formatDate(article.publishedAt)}</span>
                </CardMeta>
            </CardContent>
        </ArticleCardStyled>
    );
};


const BlogList: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [articles, setArticles] = useState<BlogArticle[]>([]);
    const [loading, setLoading] = useState(true);

    // note APIから全記事を取得（isLastPageになるまでページを辿る）
    useEffect(() => {
        const loadPosts = async () => {
            setLoading(true);
            try {
                const fetchedArticles = await fetchNotePosts();
                setArticles(fetchedArticles);
            } catch (error) {
                console.error('note記事の取得に失敗しました:', error);
            } finally {
                setLoading(false);
            }
        };
        loadPosts();
    }, []);

    // 取得済みの記事をキーワードでクライアントサイド絞り込み
    const filteredArticles = useMemo(() => {
        const keyword = searchTerm.trim().toLowerCase();
        if (!keyword) return articles;
        return articles.filter(article =>
            article.title.toLowerCase().includes(keyword) ||
            article.excerpt.toLowerCase().includes(keyword)
        );
    }, [articles, searchTerm]);

    return (
        <>
            <Helmet>
                <title>ブログ一覧 | LudyWorks</title>
                <meta name="description" content="LudyWorksのブログ記事一覧です。Web開発や技術に関する情報を発信しています。" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="ブログ一覧 | LudyWorks" />
                <meta property="og:description" content="LudyWorksのブログ記事一覧です。Web開発や技術に関する情報を発信しています。" />
                <meta property="og:url" content="https://ludyworks.com/blog" />
            </Helmet>
            <BlogListContainer
                initial={{ opacity: 0 }} // y:20 は削除し、opacityのみに
                animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }} // durationを短く
        >
            <BackToHomeButton onClick={() => navigate('/')}>
                戻る
            </BackToHomeButton>
            <BlogListGlobalTitle>LudyWorks Blog</BlogListGlobalTitle>

            <BlogHeader>
                <SearchInput
                    type="text"
                    placeholder="記事をキーワードで検索..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </BlogHeader>

            {loading ? (
                <p style={{ textAlign: 'center', fontSize: '1.2em', marginTop: '50px', color: '#a0a0a0' }}>
                    記事を読み込んでいます...
                </p>
            ) : filteredArticles.length > 0 ? (
                <ArticleGrid>
                    {filteredArticles.map(article => (
                        <ArticleCard key={article.id} article={article} />
                    ))}
                </ArticleGrid>
            ) : (
                <p style={{ textAlign: 'center', fontSize: '1.2em', marginTop: '50px', color: '#a0a0a0' }}>
                    該当する記事が見つかりませんでした。
                </p>
            )}
        </BlogListContainer>
        </>
    );
};

export default BlogList;
